import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { startGoogleLogin } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Activity, HeartPulse, LineChart, FileText, QrCode, Sparkles, BellRing, Stethoscope, MonitorSmartphone, Droplet, ArrowRight, Mail, Lock, User, Loader2 } from "lucide-react";

const features = [
  { icon: LineChart, title: "Gráficas CDC y OMS", desc: "Percentiles interactivos de somatometría." },
  { icon: Sparkles, title: "Asistencia con IA", desc: "Sugerencias de tratamiento e hitos del desarrollo." },
  { icon: FileText, title: "Recetas y estudios PDF", desc: "Genera y comparte por código QR." },
  { icon: BellRing, title: "Alarmas inteligentes", desc: "Alertas si el paciente está fuera de rango." },
];

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [sessionEnded, setSessionEnded] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("session_ended") === "other_device") {
      setSessionEnded(true);
      localStorage.removeItem("session_ended");
    }
  }, []);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleLogin = () => {
    startGoogleLogin();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register"
        ? { name: form.name, email: form.email, password: form.password }
        : { email: form.email, password: form.password };
      const { data } = await api.post(path, body);
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      setUser(data.user);
      toast.success(mode === "register" ? "Cuenta creada. ¡Bienvenido!" : "Sesión iniciada");
      window.location.replace("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map((x) => x.msg).join(" ") : "No se pudo completar. Intenta de nuevo.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email, origin: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      toast.error("No se pudo enviar el correo. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const goMode = (m) => { setMode(m); setForgotSent(false); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background overflow-hidden">
      {/* Left - brand / hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-brand-ink overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-terracotta/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-brand-sage/30 blur-3xl" />
        <Logo size={48} className="relative z-10 [&_span]:text-white [&_.text-brand-ink]:text-white" />

        <div className="relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-outfit text-4xl xl:text-5xl font-extrabold text-white leading-tight tracking-tight"
          >
            El expediente pediátrico
            <span className="text-brand-sand"> inteligente</span> que cuida a tus pacientes.
          </motion.h1>
          <p className="mt-5 text-white/70 font-nunito text-lg max-w-md">
            Expedientes automáticos, somatometría con percentiles, recetas con QR y asistencia
            clínica con IA enfocada en el paciente pediátrico.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-10 max-w-lg">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10"
              >
                <f.icon className="text-brand-sand mb-2" size={22} />
                <p className="text-white font-outfit font-semibold text-sm">{f.title}</p>
                <p className="text-white/60 text-xs mt-1">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
        <p className="relative z-10 text-white/40 text-sm">Uso clínico profesional · Dr. Néstor Gibran López Díaz</p>
      </div>

      {/* Right - login */}
      <div className="flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-10 flex justify-center"><Logo size={48} /></div>
          {sessionEnded && (
            <div data-testid="session-ended-alert"
              className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-800">
              <MonitorSmartphone size={20} className="shrink-0 mt-0.5" />
              <div>
                <p className="font-outfit font-semibold text-sm">Tu sesión se cerró</p>
                <p className="text-xs mt-0.5 text-amber-700">Se inició sesión en otro dispositivo. Por seguridad, ClinPed permite una sola sesión activa. Vuelve a iniciar sesión para continuar.</p>
              </div>
            </div>
          )}
          <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(61,64,91,0.06)] border border-stone-100">
            <div className="w-24 h-24 rounded-full overflow-hidden mb-5 ring-2 ring-brand-sand/60 bg-brand-sand/20">
              <img src="https://customer-assets.emergentagent.com/job_ai-pediatric-hub/artifacts/dq44qbby_IMG_0358.jpeg"
                alt="Doctor ClinPed" className="w-full h-full object-cover" data-testid="login-doctor-illustration" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-brand-ink">Bienvenido, Doctor</h2>
            <p className="text-muted-foreground mt-2 font-nunito">
              Inicia sesión para acceder a tus expedientes y herramientas clínicas.
            </p>

            {mode === "forgot" ? (
              <div className="mt-7" data-testid="forgot-section">
                {forgotSent ? (
                  <div data-testid="forgot-sent" className="rounded-2xl border border-brand-sage/40 bg-brand-sage/10 p-5 text-center">
                    <Mail size={28} className="mx-auto text-brand-sage mb-2" />
                    <p className="font-outfit font-semibold text-brand-ink">Revisa tu correo</p>
                    <p className="text-sm text-brand-ink/70 mt-1">
                      Si <b>{form.email}</b> está registrado, te enviamos un enlace para restablecer tu contraseña. El enlace expira en 1 hora.
                    </p>
                    <button type="button" data-testid="forgot-back" onClick={() => goMode("login")}
                      className="mt-4 text-brand-terracotta font-outfit font-semibold hover:underline">
                      Volver a iniciar sesión
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgot} data-testid="forgot-form" className="space-y-3">
                    <p className="text-sm text-brand-ink/70">Escribe tu correo y te enviaremos un enlace para crear una nueva contraseña.</p>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
                      <input
                        data-testid="forgot-email-input"
                        type="email" required value={form.email} onChange={set("email")}
                        placeholder="correo@ejemplo.com"
                        className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 bg-stone-50 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40 focus:border-brand-terracotta/40"
                      />
                    </div>
                    <button
                      data-testid="forgot-submit-btn"
                      type="submit" disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 bg-brand-ink hover:bg-brand-ink/90 text-white font-outfit font-semibold py-3.5 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100"
                    >
                      {submitting && <Loader2 size={18} className="animate-spin" />}
                      Enviar enlace
                    </button>
                    <button type="button" data-testid="forgot-cancel" onClick={() => goMode("login")}
                      className="w-full text-center text-sm text-brand-ink/60 hover:text-brand-ink">
                      Cancelar
                    </button>
                  </form>
                )}
              </div>
            ) : (
            <>
            <button
              data-testid="google-login-btn"
              onClick={handleLogin}
              className="mt-7 w-full flex items-center justify-center gap-3 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold py-3.5 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-brand-terracotta/20"
            >
              <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#fff" d="M12 11v3.6h5.1c-.2 1.3-1.6 3.9-5.1 3.9-3.1 0-5.6-2.6-5.6-5.7S8.9 7 12 7c1.8 0 2.9.7 3.6 1.4l2.4-2.4C16.5 4.6 14.5 3.7 12 3.7 6.9 3.7 2.8 7.8 2.8 13S6.9 22.3 12 22.3c6.6 0 9-4.6 9-7 0-.5 0-.9-.1-1.3H12z"/></svg>
              Continuar con Google
            </button>

            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-stone-200" />
              o con tu correo
              <div className="h-px flex-1 bg-stone-200" />
            </div>

            <form onSubmit={handleEmailSubmit} data-testid="email-auth-form" className="space-y-3">
              {mode === "register" && (
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
                  <input
                    data-testid="auth-name-input"
                    type="text" required value={form.name} onChange={set("name")}
                    placeholder="Nombre completo"
                    className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 bg-stone-50 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40 focus:border-brand-terracotta/40"
                  />
                </div>
              )}
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
                <input
                  data-testid="auth-email-input"
                  type="email" required value={form.email} onChange={set("email")}
                  placeholder="correo@ejemplo.com"
                  className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 bg-stone-50 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40 focus:border-brand-terracotta/40"
                />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" />
                <input
                  data-testid="auth-password-input"
                  type="password" required value={form.password} onChange={set("password")}
                  placeholder={mode === "register" ? "Contraseña (mín. 8 caracteres)" : "Contraseña"}
                  className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 bg-stone-50 text-sm font-nunito focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40 focus:border-brand-terracotta/40"
                />
              </div>
              {mode === "login" && (
                <div className="text-right">
                  <button type="button" data-testid="forgot-link" onClick={() => goMode("forgot")}
                    className="text-xs text-brand-ink/60 hover:text-brand-terracotta hover:underline">
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
              )}
              <button
                data-testid="auth-submit-btn"
                type="submit" disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-brand-ink hover:bg-brand-ink/90 text-white font-outfit font-semibold py-3.5 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:hover:scale-100"
              >
                {submitting && <Loader2 size={18} className="animate-spin" />}
                {mode === "register" ? "Crear cuenta" : "Iniciar sesión"}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-brand-ink/60">
              {mode === "register" ? "¿Ya tienes cuenta?" : "¿Aún no tienes cuenta?"}{" "}
              <button
                data-testid="auth-toggle-mode"
                type="button"
                onClick={() => goMode(mode === "register" ? "login" : "register")}
                className="text-brand-terracotta font-outfit font-semibold hover:underline"
              >
                {mode === "register" ? "Inicia sesión" : "Crea una gratis"}
              </button>
            </p>
            </>
            )}

            <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground justify-center">
              <HeartPulse size={14} className="text-brand-sage" />
              Datos protegidos · Solo personal médico
            </div>
            <div className="mt-3 text-center">
              <a href="/privacidad" data-testid="privacy-link" className="text-xs text-muted-foreground underline hover:text-brand-ink">Aviso de Privacidad</a>
            </div>
          </div>

          <a href="/cliniv.html" target="_blank" rel="noreferrer" data-testid="login-cliniv-promo"
            className="group mt-5 block rounded-3xl p-5 bg-gradient-to-br from-[#0b5f73] to-[#0e7d92] text-white shadow-lg shadow-[#0b5f73]/25 transition-all hover:scale-[1.02]">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
                <Droplet size={24} className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-outfit font-extrabold text-base">ClinIV</p>
                  <span className="text-[10px] font-bold bg-white/20 rounded-full px-2 py-0.5">GRATIS</span>
                </div>
                <p className="text-white/80 text-xs mt-0.5">Calculadora de soluciones intravenosas pediátricas. Ábrela sin registrarte.</p>
              </div>
              <ArrowRight size={18} className="ml-auto shrink-0 transition-transform group-hover:translate-x-1" />
            </div>
          </a>

          <div className="mt-5 text-center">
            <p className="text-sm text-brand-ink/60">¿Eres paciente?</p>
            <button data-testid="login-directory-link" onClick={() => navigate("/directorio")}
              className="mt-1 inline-flex items-center gap-2 text-teal-600 font-outfit font-semibold hover:underline">
              <Stethoscope size={16} /> Contacta a tu médico
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
